import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSeoMeta } from "@/hooks/useSeoMeta";

export default function MentionsLegalesPage() {
  useSeoMeta({
    title: "Mentions Légales — Wazi Stories Afrique",
    description: "Mentions légales de Wazi Stories Afrique : éditeur, hébergeur, propriété intellectuelle, agrégation de contenus, politique de confidentialité et contact.",
    type: "website",
    keywords: "mentions légales Wazi Stories, politique confidentialité, agrégateur contenus africains",
  });

  const sections = [
    {
      id: "editeur",
      icon: "ri-building-line",
      title: "1. Éditeur du site",
      content: (
        <div className="space-y-2 text-gray-600 text-sm leading-relaxed">
          <p><strong className="text-gray-800">Nom de la plateforme :</strong> Wazi Stories Afrique</p>
          <p><strong className="text-gray-800">Société :</strong> Wazi Consulting</p>
          <p><strong className="text-gray-800">Siège social :</strong> Abidjan, Côte d&apos;Ivoire</p>
          <p><strong className="text-gray-800">Email :</strong>{" "}
            <a href="mailto:contact@waziconsulting.com" className="text-orange-500 hover:underline cursor-pointer">
              contact@waziconsulting.com
            </a>
          </p>
          <p><strong className="text-gray-800">Directeur de la publication :</strong> Équipe Wazi Stories</p>
        </div>
      ),
    },
    {
      id: "hebergeur",
      icon: "ri-server-line",
      title: "2. Hébergement",
      content: (
        <p className="text-gray-600 text-sm leading-relaxed">
          Ce site est hébergé par <strong className="text-gray-800">Readdy.ai</strong>, plateforme de création et d&apos;hébergement de sites web.
          Pour toute question relative à l&apos;hébergement, vous pouvez contacter Readdy.ai via leur site officiel.
        </p>
      ),
    },
    {
      id: "agregateur",
      icon: "ri-rss-line",
      title: "3. Nature du service — Agrégateur de contenus",
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>
            <strong className="text-orange-500">Wazi Stories Afrique est un agrégateur de contenus éditoriaux.</strong>{" "}
            Notre plateforme collecte, sélectionne et met en avant des articles, interviews et portraits d&apos;entrepreneurs africains
            publiés par des médias, blogs et plateformes tiers reconnus.
          </p>
          <p>
            Wazi Stories <strong className="text-gray-800">ne revendique pas la paternité</strong> des contenus originaux.
            Chaque article affiché sur notre plateforme est accompagné du nom de la source d&apos;origine et d&apos;un lien
            renvoyant vers l&apos;article original publié par le média tiers.
          </p>
          <p>
            Les sources utilisées incluent notamment : TechCrunch, BBC, CNN, Forbes, TechCabal, Jeune Afrique, et d&apos;autres
            médias spécialisés dans l&apos;entrepreneuriat africain.
          </p>
          <p>
            Wazi Stories s&apos;efforce de respecter les droits des auteurs et éditeurs originaux. Si vous êtes auteur ou éditeur
            et souhaitez que votre contenu soit retiré de notre plateforme, veuillez nous contacter à{" "}
            <a href="mailto:contact@waziconsulting.com" className="text-orange-500 hover:underline cursor-pointer">
              contact@waziconsulting.com
            </a>.
          </p>
        </div>
      ),
    },
    {
      id: "propriete",
      icon: "ri-copyright-line",
      title: "4. Propriété intellectuelle",
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>
            La structure générale du site Wazi Stories Afrique, son design, son logo, ses éléments graphiques et son code source
            sont la propriété exclusive de Wazi Consulting et sont protégés par les lois en vigueur sur la propriété intellectuelle.
          </p>
          <p>
            Les contenus éditoriaux (articles, interviews, portraits) affichés sur la plateforme restent la propriété de leurs
            auteurs et éditeurs respectifs. Wazi Stories ne transfère aucun droit de propriété sur ces contenus.
          </p>
          <p>
            Toute reproduction, représentation, modification ou exploitation non autorisée des éléments propres à Wazi Stories
            est strictement interdite.
          </p>
        </div>
      ),
    },
    {
      id: "donnees",
      icon: "ri-shield-user-line",
      title: "5. Données personnelles & Confidentialité",
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>
            Wazi Stories Afrique s&apos;engage à protéger la vie privée de ses utilisateurs conformément aux réglementations
            applicables en matière de protection des données personnelles.
          </p>
          <p>
            <strong className="text-gray-800">Données collectées :</strong> Dans le cadre de l&apos;utilisation du site,
            nous pouvons collecter des données telles que l&apos;adresse email (via le formulaire de newsletter ou de contact),
            les données de navigation (cookies analytiques) et les informations soumises via le formulaire de soumission d&apos;article.
          </p>
          <p>
            <strong className="text-gray-800">Utilisation des données :</strong> Ces données sont utilisées exclusivement
            pour l&apos;envoi de la newsletter, la gestion des demandes de contact et l&apos;amélioration de l&apos;expérience utilisateur.
            Elles ne sont jamais vendues ni transmises à des tiers à des fins commerciales.
          </p>
          <p>
            <strong className="text-gray-800">Droits des utilisateurs :</strong> Conformément aux lois applicables, vous disposez
            d&apos;un droit d&apos;accès, de rectification et de suppression de vos données personnelles. Pour exercer ces droits,
            contactez-nous à{" "}
            <a href="mailto:contact@waziconsulting.com" className="text-orange-500 hover:underline cursor-pointer">
              contact@waziconsulting.com
            </a>.
          </p>
        </div>
      ),
    },
    {
      id: "cookies",
      icon: "ri-settings-3-line",
      title: "6. Cookies",
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>
            Le site Wazi Stories Afrique peut utiliser des cookies techniques nécessaires au bon fonctionnement de la plateforme
            (préférences de langue, session utilisateur) ainsi que des cookies analytiques pour mesurer l&apos;audience du site.
          </p>
          <p>
            Vous pouvez configurer votre navigateur pour refuser les cookies. Cela peut toutefois affecter certaines fonctionnalités
            du site.
          </p>
        </div>
      ),
    },
    {
      id: "responsabilite",
      icon: "ri-error-warning-line",
      title: "7. Limitation de responsabilité",
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>
            Wazi Stories Afrique s&apos;efforce de fournir des informations exactes et à jour. Cependant, en tant qu&apos;agrégateur
            de contenus, nous ne pouvons garantir l&apos;exactitude, l&apos;exhaustivité ou l&apos;actualité des informations
            provenant de sources tierces.
          </p>
          <p>
            Wazi Stories ne saurait être tenu responsable des erreurs ou omissions dans les contenus agrégés, ni des dommages
            directs ou indirects résultant de l&apos;utilisation de ces informations.
          </p>
          <p>
            Les liens vers des sites externes sont fournis à titre informatif. Wazi Stories n&apos;est pas responsable du contenu
            des sites tiers vers lesquels ces liens renvoient.
          </p>
        </div>
      ),
    },
    {
      id: "contact",
      icon: "ri-mail-send-line",
      title: "8. Contact & Signalement",
      content: (
        <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
          <p>
            Pour toute question relative aux présentes mentions légales, pour signaler un contenu ou exercer vos droits,
            vous pouvez nous contacter :
          </p>
          <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2">
              <i className="ri-mail-line text-orange-500"></i>
              <a href="mailto:contact@waziconsulting.com" className="text-orange-500 font-semibold hover:underline cursor-pointer text-sm">
                contact@waziconsulting.com
              </a>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-map-pin-line text-orange-500"></i>
              <span className="text-sm">Abidjan, Côte d&apos;Ivoire</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-whatsapp-line text-green-500"></i>
              <span className="text-sm">WhatsApp Business disponible</span>
            </div>
          </div>
          <p className="text-xs text-gray-400 italic">
            Nous nous engageons à répondre à toute demande de retrait de contenu dans un délai de 72 heures ouvrées.
          </p>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#fdf8f3]">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-10 bg-white border-b border-gray-100">
        <div className="max-w-[900px] mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-6">
            <Link to="/" className="hover:text-orange-500 transition-colors cursor-pointer">Accueil</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-600">Mentions Légales</span>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-orange-100">
              <i className="ri-scales-3-line text-orange-500 text-xl"></i>
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 font-serif">Mentions Légales</h1>
              <p className="text-sm text-gray-400 mt-0.5">Dernière mise à jour : avril 2026</p>
            </div>
          </div>
          <p className="text-gray-500 text-sm leading-relaxed max-w-2xl">
            Conformément aux dispositions légales en vigueur, voici les informations légales relatives à la plateforme
            Wazi Stories Afrique, agrégateur de contenus dédié à l&apos;entrepreneuriat africain.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-[900px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

            {/* Sidebar nav */}
            <aside className="hidden lg:block lg:col-span-1">
              <div className="sticky top-24 bg-white rounded-2xl border border-gray-100 p-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Sommaire</p>
                <nav className="space-y-1">
                  {sections.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="flex items-center gap-2 text-xs text-gray-500 hover:text-orange-500 py-1.5 px-2 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer"
                    >
                      <i className={`${s.icon} text-orange-400 text-sm`}></i>
                      <span className="line-clamp-1">{s.title.replace(/^\d+\.\s/, "")}</span>
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <div className="lg:col-span-3 space-y-6">
              {/* Disclaimer banner */}
              <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5 flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-orange-500 text-lg"></i>
                </div>
                <p className="text-sm text-orange-800 leading-relaxed">
                  <strong>Wazi Stories est un agrégateur de contenus.</strong> Nous ne sommes pas auteurs des articles
                  et interviews publiés sur cette plateforme. Chaque contenu est sourcé depuis des médias tiers reconnus
                  et redirige vers la source originale.
                </p>
              </div>

              {sections.map((s) => (
                <div
                  key={s.id}
                  id={s.id}
                  className="bg-white rounded-2xl border border-gray-100 p-6 scroll-mt-24"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-orange-50">
                      <i className={`${s.icon} text-orange-500`}></i>
                    </div>
                    <h2 className="text-base font-bold text-gray-900">{s.title}</h2>
                  </div>
                  {s.content}
                </div>
              ))}

              <p className="text-xs text-gray-400 text-center pt-4">
                Ces mentions légales sont susceptibles d&apos;être modifiées à tout moment. La version en vigueur est celle
                accessible en ligne sur cette page.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
